<script setup lang="ts">
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import type { ReturnedRoll } from '@/types/batch-types'
import { unitOptions } from '@/constants/dimension'
import Dropdown from 'primevue/dropdown'
import { Icon } from '@iconify/vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {navigateToBatchByRollId} from '@/utils/batch-navigation'

const props = withDefaults(defineProps<{
  editable?: boolean
  showActions?: boolean
  currentBatchNumber?: string
  showOriginTags?: boolean
}>(), {
  editable: false,
  showActions: true,
  showOriginTags: false
})

const router = useRouter()

const emit = defineEmits<{
  remove: [value: ReturnedRoll]
}>()

const model = defineModel<ReturnedRoll>('modelValue', {
  required: true
})

const isNavigatingToOriginal = ref(false)

const id = (propertyName: string) => `roll-${model.value.id}-${propertyName}`

const shouldShowOriginalBatch = computed(() => {
  return props.showOriginTags &&
      model.value.originalBatchNumber &&
      model.value.originalBatchNumber !== props.currentBatchNumber
})

const navigateToOriginalBatch = (event: Event) => {
  event.stopPropagation()
  event.preventDefault()

  if (isNavigatingToOriginal.value) return

  if (model.value.previousRollId) {
    isNavigatingToOriginal.value = true

    setTimeout(async () => {
      try {
        await navigateToBatchByRollId(router, model.value.previousRollId as string)
      } catch (error) {
        console.error('Navigation failed:', error)
      } finally {
        isNavigatingToOriginal.value = false
      }
    }, 0)
  } else {
    console.warn('No previousRollId found')
  }
}
</script>

<template>
  <div class="grid grid-cols-12 gap-x-10">
    <div class="col-span-2">
      <label :for="id('rollNumber')" class="text-xs">Roll No.</label>
      <InputText
        v-model="model.rollNumber"
        :disabled="!editable"
        :id="id('rollNumber')"
        class="w-full text-sm h-8"
        input-class="max-w-full"
      />
    </div>
    <div class="col-span-2">
      <label :for="id('newLengthValue')" class="text-xs">New Length</label>
      <InputNumber
        v-model="model.newLengthValue"
        :disabled="!editable"
        :id="id('newLengthValue')"
        class="w-full text-sm h-8"
        input-class=" max-w-full"
      />
    </div>
    <div class="col-span-2">
      <label :for="id('newLengthUnit')" class="text-xs">New Unit</label>
      <Dropdown
        v-model="model.newLengthUnit"
        :disabled="!editable"
        class="w-full text-sm h-8"
        :id="id('newLengthUnit')"
        :options="unitOptions"
        optionLabel="label"
        optionValue="value"
        type="number"
      />
    </div>
    <div class="col-span-2">
      <label :for="id('newWeight')" class="text-xs"> New Weight</label>
      <InputNumber
        v-model="model.newWeight"
        :disabled="!editable"
        :id="id('newWeight')"
        class="w-full text-sm h-8"
        input-class="max-w-full"
      />
    </div>

    <div class="col-span-4 flex items-end">
      <div class="flex flex-col gap-1">
        <Tag
            v-if="shouldShowOriginalBatch"
            severity="secondary"
            :class="[
            '!px-2 !py-1 text-xs cursor-pointer transition-colors select-none',
            isNavigatingToOriginal
              ? 'opacity-50 cursor-wait'
              : 'hover:bg-gray-200'
          ]"
            @click.stop.prevent="navigateToOriginalBatch"
            @mousedown.stop
            @mouseup.stop
        >
          <Icon
              :icon="isNavigatingToOriginal ? 'mdi:loading' : 'mdi:package-variant-closed'"
              :class="['text-sm me-1', isNavigatingToOriginal ? 'animate-spin' : '']"
          />
          Origin: {{ model.originalBatchNumber }}
        </Tag>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
